// Client-side downloadable copy of the member's signed Contribution Agreement.
// No backend, no storage — built in the browser from data already on the
// dashboard. jsPDF is lazy-loaded (dynamic import) inside the handler so its
// ~350KB only downloads when a member clicks; the main bundle is unaffected.
//
// VERSION-FIDELITY LIMITATION (deliberate, documented): this renders the CURRENT
// agreement text from agreementTerms.js. That is a non-issue today — there are no
// real members yet and onboarding is forward-only, so every member signs the
// current version and the stored terms_sha256 matches this text. IF
// AGREEMENT_VERSION is bumped AFTER members have signed an older version, an
// old-signer's PDF would render the CURRENT text alongside their OLD stored hash,
// a visible mismatch. Resolving that then requires versioned terms snapshots
// (render the exact text they signed). This is a known limitation, not a latent
// bug under current conditions.
//
// Privacy Policy and Terms & Conditions are SEPARATE documents whose full text
// lives on the hosted pages (/privacy.html, /terms.html), not in the agreement.
// The PDF states the VERSIONS the member accepted, matching the consent record.
import {
  AGREEMENT_TITLE,
  AGREEMENT_SECTIONS,
  AGREEMENT_DISCLAIMERS_TITLE,
  AGREEMENT_DISCLAIMERS,
} from '../../onboarding/constants/agreementTerms';

export async function downloadAgreementPdf({
  fullName, signature, agreedAt, version, sha256, privacyVersion, tcVersion,
}) {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });

  const margin = 56;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const maxW = pageW - margin * 2;
  let y = margin;

  const ensureSpace = (needed) => {
    if (y + needed > pageH - margin) { doc.addPage(); y = margin; }
  };
  const write = (text, { size = 11, style = 'normal', gap = 8, lineH = 15 } = {}) => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    for (const line of doc.splitTextToSize(String(text), maxW)) {
      ensureSpace(lineH);
      doc.text(line, margin, y);
      y += lineH;
    }
    y += gap;
  };

  // Title
  write(AGREEMENT_TITLE, { size: 18, style: 'bold', gap: 12, lineH: 22 });

  // Sections (heading + body)
  for (const s of AGREEMENT_SECTIONS) {
    write(s.heading, { size: 12, style: 'bold', gap: 2, lineH: 16 });
    write(s.body, { size: 11, style: 'normal', gap: 10 });
  }

  // Disclaimers
  write(AGREEMENT_DISCLAIMERS_TITLE, { size: 12, style: 'bold', gap: 2, lineH: 16 });
  for (const d of AGREEMENT_DISCLAIMERS) {
    write(`- ${d}`, { size: 11, style: 'normal', gap: 3 });
  }

  // Acknowledgment block
  y += 6;
  ensureSpace(24);
  doc.setDrawColor(180);
  doc.line(margin, y, pageW - margin, y);
  y += 18;
  write('Acknowledgment', { size: 12, style: 'bold', gap: 6, lineH: 16 });

  const ack = [
    `Signed by: ${fullName || '—'}`,
    `Signature: ${signature || '—'}`,
    `Date agreed: ${agreedAt ? new Date(agreedAt).toLocaleString() : '—'}`,
    `Agreement version: ${version || '—'}`,
    privacyVersion ? `Privacy Policy accepted: ${privacyVersion}` : null,
    tcVersion ? `Terms & Conditions accepted: ${tcVersion}` : null,
    `Terms hash (SHA-256): ${sha256 || '—'}`,
  ].filter(Boolean);
  for (const line of ack) write(line, { size: 10, style: 'normal', gap: 3, lineH: 14 });

  write(
    'The Privacy Policy and Terms & Conditions are separate documents hosted online. This record states the versions accepted.',
    { size: 9, style: 'italic', gap: 0, lineH: 12 },
  );

  doc.save(`esusol-agreement-${version || 'record'}.pdf`);
}
